import { BOARD_DRAWING, type Square, type Vec2 } from '@chessshooter/shared';
import { squareCenter, type BoardLayout } from './boardLayout';

/** Equal endpoints mark a square; different endpoints form a directed arrow. */
export interface BoardDrawing { from: Square; to: Square }

export class BoardDrawings {
  private marks: BoardDrawing[] = [];
  private gesture: { id: number; from: Square; to: Square | null } | null = null;

  start(id: number, from: Square): void {
    if (!this.gesture) this.gesture = { id, from, to: from };
  }

  move(id: number, to: Square | null): void {
    if (this.gesture?.id === id) this.gesture.to = to;
  }

  finish(id: number, to: Square | null): void {
    const g = this.gesture;
    if (!g || g.id !== id) return;
    this.gesture = null;
    if (!to) return;
    const exists = this.marks.some((m) => m.from === g.from && m.to === to);
    this.marks = exists ? this.marks.filter((m) => m.from !== g.from || m.to !== to) : [...this.marks, { from: g.from, to }];
  }

  cancel(id?: number): void {
    if (id === undefined || this.gesture?.id === id) this.gesture = null;
  }

  clear(): void { this.cancel(); this.marks = []; }

  view(): readonly BoardDrawing[] {
    const g = this.gesture;
    return g?.to && !this.marks.some((m) => m.from === g.from && m.to === g.to)
      ? [...this.marks, { from: g.from, to: g.to }] : this.marks;
  }
}

/** Rogess separates shared tips and collinear lanes so every arrow stays visible. */
export function drawingArrows(drawings: readonly BoardDrawing[], layout: BoardLayout) {
  const S = BOARD_DRAWING, scale = layout.square;
  const arrows = drawings.filter((m) => m.from !== m.to).map((m) => {
    const start = squareCenter(layout, m.from), target = squareCenter(layout, m.to);
    const dx = target.x - start.x, dy = target.y - start.y, length = Math.hypot(dx, dy);
    return { ...m, start, target, ux: dx / length, uy: dy / length, length };
  });
  return arrows.map((arrow) => {
    const { start, target, ux, uy } = arrow;
    const shared = arrows.filter((other) => other.to === arrow.to);
    const aligned = shared.filter((other) => Math.abs(other.ux - ux) + Math.abs(other.uy - uy) < Number.EPSILON * 4)
      .sort((a, b) => a.length - b.length);
    const lane = (aligned.indexOf(arrow) - (aligned.length - 1) / 2) * S.sharedLaneGap * scale;
    const inset = (shared.length > 1 ? S.sharedTipInset : S.tipInset) * scale;
    const tip = { x: target.x - ux * inset - uy * lane, y: target.y - uy * inset + ux * lane };
    const length = Math.hypot(tip.x - start.x, tip.y - start.y);
    const ax = (tip.x - start.x) / length, ay = (tip.y - start.y) / length;
    const base = { x: tip.x - ax * S.headLength * scale, y: tip.y - ay * S.headLength * scale };
    const px = -ay * S.headHalfWidth * scale, py = ax * S.headHalfWidth * scale;
    const head: Vec2[] = [tip, { x: base.x + px, y: base.y + py }, { x: base.x - px, y: base.y - py }];
    return { start, base, head };
  });
}

export function drawBoardDrawings(ctx: CanvasRenderingContext2D, drawings: readonly BoardDrawing[], layout: BoardLayout): void {
  const S = BOARD_DRAWING, scale = layout.square;
  ctx.save();
  ctx.beginPath();
  ctx.rect(layout.x, layout.y, layout.size, layout.size);
  ctx.clip();
  ctx.globalAlpha = S.opacity;
  ctx.strokeStyle = S.color;
  ctx.fillStyle = S.color;
  ctx.lineWidth = S.circleWidth * scale;
  for (const mark of drawings) if (mark.from === mark.to) {
    const c = squareCenter(layout, mark.from);
    ctx.beginPath(); ctx.arc(c.x, c.y, S.circleRadius * scale, 0, Math.PI * 2); ctx.stroke();
  }
  const arrows = drawingArrows(drawings, layout);
  ctx.lineWidth = S.shaftWidth * scale;
  ctx.lineCap = 'round';
  for (const { start, base } of arrows) {
    ctx.beginPath(); ctx.moveTo(start.x, start.y); ctx.lineTo(base.x, base.y); ctx.stroke();
  }
  // All heads sit above all shafts, with a pale outline at converging tips.
  ctx.strokeStyle = S.outline;
  ctx.lineWidth = S.headOutline * scale;
  ctx.lineJoin = 'round';
  for (const { head } of arrows) {
    ctx.beginPath(); ctx.moveTo(head[0].x, head[0].y);
    for (const point of head.slice(1)) ctx.lineTo(point.x, point.y);
    ctx.closePath(); ctx.stroke(); ctx.fill();
  }
  ctx.restore();
}
